import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** Delay before treating a still-paused, ready video as an autoplay block. */
const AUTOPLAY_CHECK_DELAY = 350;

/**
 * iOS Low Power Mode often never reaches canplay. After this delay, a paused
 * autoplay video is treated as blocked even without HAVE_FUTURE_DATA.
 */
const AUTOPLAY_FALLBACK_DELAY = 1000;

/**
 * A frame or two of play-then-pause is still a policy block. Only a real
 * stretch of playback means autoplay succeeded.
 */
const SUSTAINED_PLAYBACK_SECONDS = 0.5;

/**
 * @typedef {Object} HeroVideoControlsRefs
 * @property {HTMLButtonElement[]} playPauseButton
 * @property {HTMLButtonElement} muteButton
 */

/**
 * Play/pause and mute/unmute controls for hero videos.
 * @extends {Component<HeroVideoControlsRefs>}
 */
class HeroVideoControls extends Component {
  requiredRefs = ['playPauseButton', 'muteButton'];

  /** @type {AbortController | null} */
  #abortController = null;

  /**
   * Pause from the control or reduced motion — not an autoplay policy block.
   * @type {boolean}
   */
  #intentionalPause = false;

  /**
   * True only after autoplay was blocked (Low Power Mode, etc.).
   * @type {boolean}
   */
  #autoplayBlocked = false;

  /** @type {number} */
  #autoplayCheckTimer = 0;

  /** @type {number} */
  #autoplayFallbackTimer = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    for (const video of this.#allVideos()) {
      video.playsInline = true;
      video.addEventListener('play', this.#onPlay, { signal });
      video.addEventListener('pause', this.#syncPlayback, { signal });
      video.addEventListener('canplay', this.#scheduleAutoplayCheck, { signal });
      video.addEventListener('loadedmetadata', this.#scheduleAutoplayCheck, { signal });
      video.addEventListener('stalled', this.#scheduleAutoplayFallback, { signal });
      video.addEventListener('suspend', this.#scheduleAutoplayFallback, { signal });
      video.addEventListener('error', this.#scheduleAutoplayFallback, { signal });
      video.addEventListener('volumechange', this.#syncMute, { signal });
    }

    window
      .matchMedia('(prefers-reduced-motion: reduce)')
      .addEventListener('change', this.#onReducedMotionChange, { signal });
    window.matchMedia('(min-width: 750px)').addEventListener('change', this.#syncVisibleState, { signal });

    this.#hero()?.addEventListener('click', this.#onHeroClick, { signal });

    if (prefersReducedMotion()) {
      this.#pauseForReducedMotion();
    }

    this.#syncVisibleState();
  }

  #syncVisibleState = () => {
    // data-show-ui="false": keep the host for autoplay/breakpoint JS, hide buttons.
    const showUi = this.dataset.showUi !== 'false';
    this.hidden = !showUi || this.#visibleVideos().length === 0;
    this.#silenceHiddenVideos();
    this.#resumeVisibleAutoplay();
    this.#syncPlayback();
    this.#syncMute();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearAutoplayTimers();
    this.#setPausedOverlay(false);
    this.#abortController?.abort();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #hero() {
    return this.closest('.hero');
  }

  /**
   * @returns {HTMLVideoElement[]}
   */
  #allVideos() {
    const hero = this.#hero();
    if (!hero) return [];

    return /** @type {HTMLVideoElement[]} */ ([...hero.querySelectorAll('video.hero__media')]);
  }

  /**
   * @returns {boolean}
   */
  #isDesktopBreakpoint() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  /**
   * Wrapper modifiers plus the 750px query — not `offsetParent`.
   * `matchMedia` can fire before `display: none` has laid out.
   * @param {HTMLVideoElement} video
   * @returns {boolean}
   */
  #isVideoVisible(video) {
    const wrapper = video.closest('.hero__media-wrapper');
    if (!wrapper) return true;

    const hasDesktop = wrapper.classList.contains('hero__media-wrapper--desktop');
    const hasMobile = wrapper.classList.contains('hero__media-wrapper--mobile');

    if (hasDesktop && hasMobile) return true;

    return this.#isDesktopBreakpoint() ? hasDesktop : hasMobile;
  }

  /**
   * @returns {HTMLVideoElement[]}
   */
  #visibleVideos() {
    return this.#allVideos().filter((video) => this.#isVideoVisible(video));
  }

  /**
   * Videos hidden by the desktop/mobile wrappers (`display: none`).
   * @returns {HTMLVideoElement[]}
   */
  #hiddenVideos() {
    return this.#allVideos().filter((video) => !this.#isVideoVisible(video));
  }

  /**
   * Mark a clip as intended to autoplay without the HTML `autoplay` attribute.
   * SortSite AccHtmlVideoAutoplay flags any `<video autoplay>` even when a
   * matching pause button exists (and after JS re-stamps autoplay at runtime).
   * Playback still starts via `play()`; `data-hero-autoplay` tracks intent for
   * Low Power Mode detection.
   * @param {HTMLVideoElement} video
   */
  #markAutoplayIntent(video) {
    video.removeAttribute('autoplay');
    video.setAttribute('data-hero-autoplay', '');
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #clearAutoplayIntent(video) {
    video.removeAttribute('autoplay');
    video.removeAttribute('data-hero-autoplay');
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #silenceVideo(video) {
    video.muted = true;
    this.#clearAutoplayIntent(video);

    if (!video.paused) {
      video.pause();
    }
  }

  /**
   * `display: none` does not stop playback. Pause, remute, and strip
   * autoplay intent so a hidden clip cannot start later or keep playing audio.
   */
  #silenceHiddenVideos() {
    for (const video of this.#hiddenVideos()) {
      this.#silenceVideo(video);
    }
  }

  /**
   * After a breakpoint change, restore muted playback on the newly visible
   * set. Skip if the visitor paused or prefers reduced motion.
   * Do not set the HTML `autoplay` attribute — scanners flag it (WCAG 2.2.2).
   */
  #resumeVisibleAutoplay() {
    if (this.#intentionalPause || prefersReducedMotion()) return;

    for (const video of this.#visibleVideos()) {
      this.#markAutoplayIntent(video);
      if (!video.paused) continue;

      video.muted = true;
      video.play().catch(() => {
        // Autoplay can still be blocked after a breakpoint change.
      });
    }
  }

  /**
   * Hidden clips can still fire `play` after metadata (display:none does not
   * cancel a pending play() from an earlier breakpoint).
   * @param {Event} event
   */
  #onPlay = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;

    if (!this.#isVideoVisible(video)) {
      this.#silenceVideo(video);
      return;
    }

    this.#syncPlayback();
  };

  /**
   * @param {MediaQueryListEvent} event
   */
  #onReducedMotionChange = (event) => {
    if (!event.matches) return;
    this.#pauseForReducedMotion();
  };

  #pauseForReducedMotion() {
    this.#intentionalPause = true;
    this.#autoplayBlocked = false;
    this.#clearAutoplayTimers();
    this.#setPausedOverlay(false);

    for (const video of this.#allVideos()) {
      this.#clearAutoplayIntent(video);
      video.pause();
    }

    this.#syncPlayback();
  }

  /**
   * Low Power Mode (and other autoplay blocks) draw a native play glyph on the
   * video. The hero text layer is `height: 100%` above that glyph, and the
   * media grid uses `pointer-events: none`, so the tap never reaches <video>.
   * Only that blocked state sets `.hero--video-paused` and hijacks hero taps;
   * reduced-motion and the pause control leave `.hero__link` alone.
   * @param {Event} event
   */
  #onHeroClick = (event) => {
    if (window.Shopify?.designMode) return;
    if (!this.#hero()?.classList.contains('hero--video-paused')) return;

    const videos = this.#visibleVideos();
    if (!videos.length || videos.some((video) => !video.paused)) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('hero-video-controls')) return;

    const interactive = target.closest('a, button, input, textarea, select, summary');
    if (interactive && !interactive.classList.contains('hero__link')) return;

    if (interactive?.classList.contains('hero__link')) {
      event.preventDefault();
    }

    this.#playVisible();
  };

  async togglePlayback() {
    const videos = this.#visibleVideos();
    if (!videos.length) return;

    if (videos.every((video) => video.paused)) {
      await this.#playVisible();
      return;
    }

    this.#intentionalPause = true;
    this.#autoplayBlocked = false;
    this.#clearAutoplayTimers();
    this.#setPausedOverlay(false);

    for (const video of videos) {
      video.pause();
    }

    this.#silenceHiddenVideos();
  }

  /**
   * Start every visible video in the same turn so Safari keeps the user gesture.
   * Sequential `await video.play()` drops the token after the first clip.
   */
  async #playVisible() {
    this.#silenceHiddenVideos();
    const videos = this.#visibleVideos();
    /** @type {Promise<void>[]} */
    const playPromises = [];

    for (const video of videos) {
      playPromises.push(video.play());
    }

    const playResults = await Promise.allSettled(playPromises);
    const played = playResults.some((result) => result.status === 'fulfilled');

    for (const result of playResults) {
      if (result.status === 'rejected') {
        console.error('Hero video play failed:', result.reason);
      }
    }

    if (played) {
      this.#intentionalPause = false;
      this.#autoplayBlocked = false;
      this.#setPausedOverlay(false);
    }
  }

  toggleMute() {
    const videos = this.#visibleVideos();
    if (!videos.length) return;

    const shouldUnmute = videos.every((video) => video.muted || video.volume === 0);

    for (const video of videos) {
      video.muted = !shouldUnmute;
    }

    this.#silenceHiddenVideos();
  }

  #syncPlayback = () => {
    const videos = this.#visibleVideos();
    const isPlaying = videos.some((video) => !video.paused);

    if (isPlaying) {
      this.#clearAutoplayTimers();
      this.#intentionalPause = false;
      this.#autoplayBlocked = false;
    } else if (!videos.length || this.#intentionalPause || prefersReducedMotion()) {
      this.#clearAutoplayTimers();
      this.#autoplayBlocked = false;
    } else {
      if (videos.some((video) => this.#canAssessAutoplay(video))) {
        this.#scheduleAutoplayCheck();
      }

      this.#scheduleAutoplayFallback();
    }

    this.#setPausedOverlay(this.#autoplayBlocked);

    const label = isPlaying
      ? this.refs.playPauseButton?.[0]?.dataset.labelPause
      : this.refs.playPauseButton?.[0]?.dataset.labelPlay;

    for (const button of this.#playPauseButtons()) {
      const playIcon = button.querySelector('.icon-play');
      const pauseIcon = button.querySelector('.icon-pause');
      const labelEl = button.querySelector('.hero-video-control__label');

      playIcon?.classList.toggle('hidden', isPlaying);
      pauseIcon?.classList.toggle('hidden', !isPlaying);
      button.classList.toggle('is-paused', !isPlaying);
      button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');

      if (label) {
        button.setAttribute('aria-label', label);
        if (labelEl) labelEl.textContent = label;
      }
    }
  };

  /**
   * One pause button per video. `ref="playPauseButton[]"` is always an array.
   * @returns {HTMLButtonElement[]}
   */
  #playPauseButtons() {
    const buttons = this.refs.playPauseButton;
    if (!Array.isArray(buttons)) return [];

    return buttons.filter((button) => button instanceof HTMLButtonElement);
  }

  /**
   * Enable the full-hero tap-to-play overlay only for autoplay blocks.
   * @param {boolean} isAutoplayBlocked
   */
  #setPausedOverlay(isAutoplayBlocked) {
    const hero = this.#hero();
    if (!hero) return;

    hero.classList.toggle('hero--video-paused', Boolean(isAutoplayBlocked) && !window.Shopify?.designMode);
  }

  #clearAutoplayTimers() {
    window.clearTimeout(this.#autoplayCheckTimer);
    this.#autoplayCheckTimer = 0;
    window.clearTimeout(this.#autoplayFallbackTimer);
    this.#autoplayFallbackTimer = 0;
  }

  #shouldSkipAutoplayCheck() {
    if (this.#autoplayBlocked || this.#intentionalPause || prefersReducedMotion() || window.Shopify?.designMode) {
      return true;
    }

    const videos = this.#visibleVideos();

    return videos.length === 0 || videos.some((video) => !video.paused);
  }

  #scheduleAutoplayCheck = () => {
    if (this.#shouldSkipAutoplayCheck() || this.#autoplayCheckTimer) return;

    this.#autoplayCheckTimer = window.setTimeout(() => {
      this.#autoplayCheckTimer = 0;
      this.#detectAutoplayBlock(false);
    }, AUTOPLAY_CHECK_DELAY);
  };

  /**
   * Low Power Mode can stall before canplay. Re-check without a readyState gate.
   */
  #scheduleAutoplayFallback = () => {
    if (this.#shouldSkipAutoplayCheck() || this.#autoplayFallbackTimer) return;

    this.#autoplayFallbackTimer = window.setTimeout(() => {
      this.#autoplayFallbackTimer = 0;
      this.#detectAutoplayBlock(true);
    }, AUTOPLAY_FALLBACK_DELAY);
  };

  /**
   * HAVE_FUTURE_DATA, or a brief play-then-pause that left current data.
   * @param {HTMLVideoElement} video
   * @returns {boolean}
   */
  #canAssessAutoplay(video) {
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return true;

    return video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.played.length > 0;
  }

  /**
   * iOS Low Power Mode can start, paint a frame, then pause. Any non-empty
   * `played` range is not proof autoplay succeeded.
   * @param {HTMLVideoElement} video
   * @returns {boolean}
   */
  #hasSustainedPlayback(video) {
    let played = 0;

    for (let index = 0; index < video.played.length; index++) {
      played += video.played.end(index) - video.played.start(index);
    }

    return played >= SUSTAINED_PLAYBACK_SECONDS;
  }

  /**
   * Videos marked for hero autoplay that never really started are a policy
   * block, not a visitor who paused or asked for reduced motion.
   * Intent uses `data-hero-autoplay` — never the HTML `autoplay` attribute.
   * @param {boolean} allowWithoutReady
   */
  #detectAutoplayBlock = (allowWithoutReady = false) => {
    if (window.Shopify?.designMode || this.#intentionalPause || prefersReducedMotion()) {
      this.#autoplayBlocked = false;
      this.#setPausedOverlay(false);
      return;
    }

    const videos = this.#visibleVideos();
    if (!videos.length) {
      this.#autoplayBlocked = false;
      this.#setPausedOverlay(false);
      return;
    }

    if (videos.some((video) => !video.paused)) {
      this.#autoplayBlocked = false;
      this.#setPausedOverlay(false);
      return;
    }

    const candidates = allowWithoutReady ? videos : videos.filter((video) => this.#canAssessAutoplay(video));
    if (!candidates.length) return;

    if (candidates.some((video) => this.#hasSustainedPlayback(video))) {
      this.#autoplayBlocked = false;
      this.#setPausedOverlay(false);
      return;
    }

    if (!candidates.some((video) => video.hasAttribute('data-hero-autoplay'))) return;

    this.#autoplayBlocked = true;
    this.#setPausedOverlay(true);
  };

  #syncMute = () => {
    const button = this.refs.muteButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const videos = this.#visibleVideos();
    const isMuted = videos.length === 0 || videos.every((video) => video.muted || video.volume === 0);
    const muteIcon = button.querySelector('.icon-mute');
    const unmuteIcon = button.querySelector('.icon-unmute');

    muteIcon?.classList.toggle('hidden', !isMuted);
    unmuteIcon?.classList.toggle('hidden', isMuted);
    button.classList.toggle('is-muted', isMuted);
    button.setAttribute('aria-pressed', isMuted ? 'true' : 'false');

    const label = isMuted ? button.dataset.labelUnmute : button.dataset.labelMute;
    if (label) button.setAttribute('aria-label', label);
  };
}

if (!customElements.get('hero-video-controls')) {
  customElements.define('hero-video-controls', HeroVideoControls);
}
